import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Plus, Trash2 } from 'lucide-react';
import { useWatchlists, useCreateWatchlist, useDeleteWatchlist } from '../api/hooks.js';
import { ApiError, type WatchlistRow } from '../api/client.js';

// SaxoTrader-style picker for the user's named watchlists. Modelled on
// ScreenerPresetPicker; simpler because a watchlist is just a name (no
// filter JSON to overwrite). The selected watchlist drives the table in
// the Watchlist panel.

export interface WatchlistPickerProps {
  /** Currently-loaded watchlist id; null means "nothing selected". */
  selectedId: string | null;
  /** Called when the user picks (or creates) a watchlist. */
  onSelect: (watchlist: WatchlistRow) => void;
}

export function WatchlistPicker({ selectedId, onSelect }: WatchlistPickerProps) {
  const { t } = useTranslation();
  const { data, isLoading } = useWatchlists();
  const createMut = useCreateWatchlist();
  const deleteMut = useDeleteWatchlist();

  const [open, setOpen] = useState(false);
  const [naming, setNaming] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    function onClick(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setNaming(false);
        setErrorMessage(null);
      }
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const watchlists = data?.data ?? [];
  const selected = watchlists.find((w) => w.id === selectedId) ?? null;
  const buttonLabel = selected?.name ?? t('panels.watchlists.picker.placeholder');

  function startNaming() {
    setNaming(true);
    setDraftName('');
    setErrorMessage(null);
  }

  function commitNew() {
    const name = draftName.trim();
    if (!name) return;
    createMut.mutate(name, {
      onSuccess: ({ data: row }) => {
        setNaming(false);
        setDraftName('');
        setOpen(false);
        onSelect(row);
      },
      onError: (err) => {
        setErrorMessage(describeError(err, t));
      },
    });
  }

  function removeWatchlist(id: string) {
    deleteMut.mutate(id, {
      onError: (err) => setErrorMessage(describeError(err, t)),
    });
  }

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={t('panels.watchlists.picker.tooltip')}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 'var(--s-1)',
          padding: '2px var(--s-2)',
          fontSize: 'var(--t-body)',
          border: '1px solid var(--hairline)',
          borderRadius: 2,
          background: 'var(--menu-surface)',
          color: 'var(--text-primary)',
          cursor: 'pointer',
          maxWidth: 280,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{buttonLabel}</span>
        <ChevronDown size={12} />
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: 4,
            minWidth: 240,
            maxWidth: 360,
            background: 'var(--menu-surface)',
            border: '1px solid var(--hairline)',
            borderRadius: 2,
            boxShadow: '0 6px 24px rgba(10,14,26,0.14), 0 2px 6px rgba(10,14,26,0.08)',
            zIndex: 9000,
            padding: 4,
            fontSize: 'var(--t-body)',
          }}
        >
          {isLoading && <Hint>{t('panels.watchlists.picker.loading')}</Hint>}
          {!isLoading && watchlists.length === 0 && (
            <Hint>{t('panels.watchlists.picker.empty')}</Hint>
          )}
          {watchlists.map((w) => (
            <div
              key={w.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto auto',
                alignItems: 'center',
                gap: 4,
                padding: '4px 6px',
                cursor: 'pointer',
                background: w.id === selectedId ? 'var(--color-accent-100)' : 'transparent',
                borderRadius: 2,
              }}
              onMouseOver={(e) => {
                if (w.id !== selectedId) e.currentTarget.style.background = 'var(--bg-sunken)';
              }}
              onMouseOut={(e) => {
                if (w.id !== selectedId) e.currentTarget.style.background = 'transparent';
              }}
            >
              <button
                type="button"
                onClick={() => {
                  onSelect(w);
                  setOpen(false);
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  textAlign: 'left',
                  font: 'inherit',
                  color: 'inherit',
                  cursor: 'pointer',
                  padding: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {w.name}
              </button>
              <span
                data-numeric
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--t-meta)',
                  color: 'var(--text-tertiary)',
                }}
              >
                {w.entityCount.toLocaleString('en-GB')}
              </span>
              <button
                type="button"
                aria-label={t('panels.watchlists.picker.delete')}
                title={t('panels.watchlists.picker.delete')}
                onClick={(e) => {
                  e.stopPropagation();
                  removeWatchlist(w.id);
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--text-tertiary)',
                  padding: 2,
                }}
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}
          <div
            style={{
              borderTop: '1px solid var(--hairline)',
              marginTop: 4,
              paddingTop: 4,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {naming ? (
              <div style={{ display: 'flex', gap: 4, padding: '2px 4px' }}>
                <input
                  autoFocus
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  placeholder={t('panels.watchlists.picker.namePlaceholder')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      commitNew();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      setNaming(false);
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: '2px 4px',
                    border: '1px solid var(--accent)',
                    borderRadius: 2,
                    background: 'var(--surface)',
                    color: 'var(--text-primary)',
                    fontSize: 'var(--t-body)',
                  }}
                />
                <button
                  type="button"
                  onClick={commitNew}
                  disabled={!draftName.trim() || createMut.isPending}
                  style={menuBtn()}
                >
                  {t('panels.watchlists.picker.create')}
                </button>
              </div>
            ) : (
              <button type="button" onClick={startNaming} style={menuBtn()}>
                <Plus size={12} style={{ marginRight: 6 }} />
                {t('panels.watchlists.picker.createNew')}
              </button>
            )}
          </div>
          {errorMessage && (
            <div
              role="alert"
              style={{
                marginTop: 6,
                padding: '4px 8px',
                background: 'color-mix(in srgb, var(--color-negative-500) 8%, transparent)',
                color: 'var(--color-negative-500)',
                fontSize: 'var(--t-meta)',
                border: '1px solid var(--color-negative-500)',
                borderRadius: 2,
              }}
            >
              {errorMessage}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function describeError(
  err: unknown,
  t: (k: string, opts?: Record<string, unknown>) => string,
): string {
  if (err instanceof ApiError) {
    if (err.status === 401) return t('panels.watchlists.picker.errorUnauth');
    if (err.status === 409) return t('panels.watchlists.picker.errorConflict');
    return t('panels.watchlists.picker.errorGeneric', { status: err.status });
  }
  return t('panels.watchlists.picker.errorOffline');
}

function menuBtn(): React.CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 6px',
    background: 'transparent',
    border: 'none',
    font: 'inherit',
    color: 'var(--text-primary)',
    cursor: 'pointer',
    borderRadius: 2,
    textAlign: 'left',
    width: '100%',
  };
}

function Hint({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: '4px 6px',
        color: 'var(--text-tertiary)',
        fontSize: 'var(--t-meta)',
      }}
    >
      {children}
    </div>
  );
}
